import { Badge } from "@bcc/components/ui/badge";
import { resolveShipToWebStatus } from "@bcc/lib/products/ship-to-countries";
import { useProductsState } from "@client-shared/providers/products";
import { useVendorContext } from "@client-shared/providers/vendor";
import { CellContext } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { FileEdit, Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangeAvailabilityPopover } from "../../change-availability-popover";
import { setStatusCellTestId } from "../products-table.utils";
import { VariantWithProductData } from "../types";

interface StatusCellProps {
  onConfigureDraft?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const STATUS_BADGE_STYLES: Record<string, string> = {
  "Visible for Sale": "bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20",
  Pending: "bg-blue-500/10 text-blue-700 dark:text-blue-400 hover:bg-blue-500/20",
  "Not Visible": "bg-gray-500/10 text-gray-700 dark:text-gray-400 hover:bg-gray-500/20",
  "Out of Stock": "bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20",
  // Canonical PIM WebStatus meaning the SKU is on the storefront but cannot
  // be bought. Presented to roasters as plain "Out of Stock" — same label,
  // same amber badge, and it shares the Out of Stock filter chip.
  "Visible but not for Sale": "bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20",
  // Not currently purchasable: presented to roasters as plain "Out of Stock".
  "Temporarily Out of Stock": "bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20",
  Rejected: "bg-red-500/10 text-red-700 dark:text-red-400 hover:bg-red-500/20",
} as const;

const WEB_STATUS_TO_STATUS_KEY: Record<string, string> = {
  "Visible for Sale": "published",
  Pending: "pending",
  "Not Visible": "notVisible",
  "Out of Stock": "outOfStock",
  "Visible but not for Sale": "outOfStock",
  "Temporarily Out of Stock": "outOfStock",
  Rejected: "rejected",
} as const;

const WEB_STATUS_TO_POPOVER_STATUS: Record<string, "published" | "out_of_stock" | "rejected" | "draft"> = {
  "Visible for Sale": "published",
  "Out of Stock": "out_of_stock",
  "Visible but not for Sale": "out_of_stock",
  "Temporarily Out of Stock": "out_of_stock",
  Rejected: "rejected",
  "Not Visible": "draft",
  Pending: "draft",
} as const;

const getStatusBadgeStyles = (webStatus: string | undefined) => STATUS_BADGE_STYLES[webStatus ?? ""] ?? STATUS_BADGE_STYLES["Pending"];

const getStatusLabel = (t: TFunction, webStatus: string | undefined): string =>
  String(t(`views.productsTable.status.${WEB_STATUS_TO_STATUS_KEY[webStatus ?? ""] ?? "pending"}`));

const webStatusToPopoverStatus = (webStatus: string | undefined): "published" | "out_of_stock" | "rejected" | "draft" =>
  WEB_STATUS_TO_POPOVER_STATUS[webStatus ?? ""] ?? "draft";

const noopUpdateSkuStatus = () => Promise.reject(new Error("Products provider not available"));

export const StatusCellMarkup = (props: CellContext<VariantWithProductData, unknown> & StatusCellProps) => {
  const { t } = useTranslation();
  // Cell reads the provider callback + isUpdating directly so the
  // parent `createColumns` memo doesn't need to depend on isUpdatingSkuStatus.
  // That's important: if isUpdating flipped through the columns array, every
  // row's cells would re-render twice per submit and ProductThumbnail's
  // local imageError state would get reset mid-flight, snapping the image
  // to the placeholder.
  const [, , productsMethods] = useProductsState();
  const [{ currentVendor }] = useVendorContext();
  const onUpdateSkuStatus = productsMethods?.handleUpdateSkuStatus ?? noopUpdateSkuStatus;
  const isUpdating = productsMethods?.isUpdatingSkuStatus ?? false;

  // One-shot lockout: once the popover reports a successful submit we
  // lock this row's trigger so the user can't fire a second duplicate
  // approval request. Resets on page refresh (component unmount).
  const [hasSubmitted, setHasSubmitted] = useState(false);

  if (!currentVendor) {
    return null;
  }

  const data = props.row.original;
  // Cross-border SKUs carry a per-country web status in `shipToInfo`. Show the
  // vendor's countryCode status (which reflects an approved Status Update),
  // falling back to the row's resolved status for non-cross-border rows.
  const webStatus = resolveShipToWebStatus(data.shipToInfo, currentVendor.countryCode, data.webStatus);
  const isPending = webStatus === "Pending";

  // Unconnected rows (no active PIM connection → no skuCode) must not
  // open the popover: without a PIM SKU the approval request would be
  // POSTed with a bogus `itemNumbers`. Mirrors the WeeklyLimitCellMarkup
  // gate, which renders an EmptyValue when `!data.skuCode`. The status
  // badge itself still renders so the row stays legible.
  const canEditStatus = !isPending && !!data.skuCode && !data.hasPendingStatusApproval;

  // Popover payload: includes the metadata the approval request needs
  // (sku, product name, coffee term, vendor key) so the popover can
  // build a RoasterApprovalRequestDraft and hand it to the provider
  // callback. Only constructed when canEditStatus is true — skuCode is
  // guaranteed defined on that branch.
  const variantForPopover = data.skuCode
    ? {
        id: data.id,
        size: data.bagSize ?? "",
        status: webStatusToPopoverStatus(webStatus),
        sku: data.skuCode,
        productName: data.productTitle ?? "",
        coffeeTerm: data.term === "Short" ? "Short" : "Long",
        vendorKey: currentVendor.vendorKey,
        roasterCountryCode: currentVendor.countryCode,
        weeklyInventory: data.weeklyInventory ?? data.maxBagsShippedPerWeek,
        shipToInfo: data.shipToInfo,
        shipTo: data.shipTo?.map((country) => country.countryCode),
        totalInventory: data.totalInventory,
        totalMaxShortTermInventory: data.totalMaxShortTermInventory,
      }
    : null;

  const renderStatusContent = () => {
    if (isPending) {
      return (
        <button type="button" onClick={props.onConfigureDraft} className="cursor-pointer flex items-center gap-1 w-fit">
          <Badge variant="secondary" className={getStatusBadgeStyles(webStatus)}>
            <FileEdit className="h-3 w-3" />
            {getStatusLabel(t, webStatus)}
          </Badge>
        </button>
      );
    }
    if (!canEditStatus || !variantForPopover) {
      return (
        <Badge variant="secondary" className={getStatusBadgeStyles(webStatus)}>
          {getStatusLabel(t, webStatus)}
        </Badge>
      );
    }
    if (hasSubmitted) {
      return (
        <button type="button" disabled className="inline-flex items-center gap-1 w-fit opacity-50 cursor-not-allowed">
          <Badge variant="secondary" className={getStatusBadgeStyles(webStatus)}>
            {getStatusLabel(t, webStatus)}
          </Badge>
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </button>
      );
    }
    return (
      <ChangeAvailabilityPopover
        variant={variantForPopover}
        onUpdateSkuStatus={onUpdateSkuStatus}
        isUpdating={isUpdating}
        onSubmitSuccess={() => setHasSubmitted(true)}
        open={props.open}
        onOpenChange={props.onOpenChange}
      >
        <button type="button" className="inline-flex items-center gap-1 w-fit cursor-pointer">
          <Badge variant="secondary" className={getStatusBadgeStyles(webStatus)}>
            {getStatusLabel(t, webStatus)}
          </Badge>
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </button>
      </ChangeAvailabilityPopover>
    );
  };

  return <div data-testid={setStatusCellTestId(props.row.index)}>{renderStatusContent()}</div>;
};
